/**
 * El texto de un aviso guardado por el server que trae clave de texto
 * (metadata.i18nKey, sus parámetros y una variante opcional): el cliente lo
 * escribe en el idioma del usuario con las plantillas del LangStore, y el título
 * y el cuerpo que mandó el server quedan como respaldo de lo que no tenga
 * plantilla.
 *
 * Plantillas: notification_<clave>_title y notification_<clave>_body, con
 * {parámetro} donde va cada valor. Con variante (por ejemplo "verified"), cada
 * parte busca primero notification_<clave>_<parte>_<variante>.
 */
export interface NotificationI18n {
  i18nKey: string;
  params: Record<string, unknown>;
  variant?: string;
}

/** La clave de texto, sus parámetros y su variante, como los guarda la campana (data). */
export function notificationI18nOf(data?: Record<string, any>): NotificationI18n | undefined {
  const i18nKey = data?.i18nKey;
  if (typeof i18nKey !== "string" || i18nKey === "") return undefined;
  const params = data?.i18nParams;
  const variant = data?.i18nVariant;
  return {
    i18nKey,
    params: params && typeof params === "object" && !Array.isArray(params) ? params : {},
    ...(typeof variant === "string" && variant !== "" ? { variant } : {}),
  };
}

const fill = (template: string, params: Record<string, unknown>): string =>
  template.replace(/\{(\w+)\}/g, (_match, name: string) => {
    const value = params[name];
    return typeof value === "string" || typeof value === "number" ? String(value) : "";
  });

function templateFor(i18n: NotificationI18n, part: "title" | "body", langStore: object): string | undefined {
  const lookup = (key: string) => (langStore as Record<string, unknown>)[key];
  const base = `notification_${i18n.i18nKey}_${part}`;
  const variant = i18n.variant ? lookup(`${base}_${i18n.variant}`) : undefined;
  if (typeof variant === "string") return variant;
  const plain = lookup(base);
  return typeof plain === "string" ? plain : undefined;
}

/** Lo que muestra la campana: las plantillas si las hay, y si no lo que mandó el server. */
export function displayedNotification(
  notification: { title: string; message: string; data?: Record<string, any> },
  langStore: object
): { title: string; message: string } {
  const i18n = notificationI18nOf(notification.data);
  if (!i18n) return { title: notification.title, message: notification.message };
  const title = templateFor(i18n, "title", langStore);
  const body = templateFor(i18n, "body", langStore);
  return {
    title: title !== undefined ? fill(title, i18n.params) : notification.title,
    message: body !== undefined ? fill(body, i18n.params) : notification.message,
  };
}
